/**
 * App themes — runtime-selectable visual identities.
 *
 * Each theme is a set of CSS custom-property overrides applied to <html> by
 * ThemeProvider (context/ThemeContext.js). The default "classic" theme uses the
 * :root values in styles/variables.css (no overrides). Alternate themes override
 * colour/gradient/glass/shadow/radius/font tokens; because gradients like
 * --gradient-primary are defined in terms of --color-* in :root, overriding the
 * colours is usually enough.
 *
 * Adding a theme = adding an entry here (and, if it needs a web font, a fontUrl).
 * The palettes mirror the Design Lab directions (constants/designDirections.js).
 */

const FONTS = {
  cinzel: 'https://fonts.googleapis.com/css2?family=Cinzel:wght@500;600;700&display=swap',
  orbitron: 'https://fonts.googleapis.com/css2?family=Orbitron:wght@500;700&display=swap',
  playfair: 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;700&display=swap'
};

export const THEMES = [
  {
    id: 'classic',
    name: 'Aesthetic Classic',
    blurb: 'The original indigo glass look.',
    swatch: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    vars: {}
  },
  {
    id: 'celestial-tarot',
    name: 'Celestial Tarot',
    blurb: 'Midnight blue & gold, mystical Cinzel serif.',
    swatch: 'linear-gradient(135deg, #6b3fbf 0%, #b18cff 60%, #f0c869 130%)',
    fontUrl: FONTS.cinzel,
    vars: {
      '--color-primary': '#b18cff',
      '--color-primary-dark': '#6b3fbf',
      '--color-primary-light': '#c9b0ff',
      '--color-secondary': '#6b3fbf',
      '--color-accent': '#f0c869',
      '--color-accent-light': '#f7dd97',
      '--color-dark': '#0c0820',
      '--color-dark-lighter': '#16102e',
      '--color-dark-light': '#241a3d',
      '--color-gray': '#3d2f63',
      '--color-gray-light': '#5a4a86',
      '--color-light': '#efe9ff',
      '--gradient-bg': 'radial-gradient(120% 120% at 50% 0%, #1b1340 0%, #0c0820 55%, #060414 100%)',
      '--gradient-dark': 'linear-gradient(135deg, #16102e 0%, #241a3d 100%)',
      '--glass-bg': 'rgba(46, 33, 90, 0.40)',
      '--glass-bg-strong': 'rgba(64, 46, 120, 0.52)',
      '--glass-border': 'rgba(177, 140, 255, 0.28)',
      '--glass-border-strong': 'rgba(177, 140, 255, 0.40)',
      '--shadow-lg': '0 16px 40px rgba(10, 4, 30, 0.6)',
      '--shadow-glow': '0 0 24px rgba(177, 140, 255, 0.45)',
      '--radius-lg': '0.875rem',
      '--radius-xl': '1.125rem',
      '--font-display': "'Cinzel', Georgia, serif"
    }
  },
  {
    id: 'aurora-glass',
    name: 'Aurora Glass',
    blurb: 'Softer aurora gradients — periwinkle, aqua, blush.',
    swatch: 'linear-gradient(135deg, #7c9dff 0%, #56d4c4 60%, #ff9ecb 120%)',
    vars: {
      '--color-primary': '#7c9dff',
      '--color-primary-dark': '#5b7de8',
      '--color-primary-light': '#9bb4ff',
      '--color-secondary': '#56d4c4',
      '--color-accent': '#ff9ecb',
      '--color-accent-light': '#ffb8d9',
      '--color-dark': '#11152b',
      '--color-dark-lighter': '#1c2340',
      '--color-dark-light': '#2a3358',
      '--color-gray': '#4a4f6e',
      '--color-gray-light': '#6a7196',
      '--color-light': '#f4f6ff',
      '--gradient-bg': 'linear-gradient(160deg, #3b2f63 0%, #2a3a72 45%, #1f5e7a 100%)',
      '--gradient-dark': 'linear-gradient(135deg, #1c2340 0%, #243a52 100%)',
      '--glass-bg': 'rgba(255, 255, 255, 0.08)',
      '--glass-bg-strong': 'rgba(255, 255, 255, 0.14)',
      '--glass-border': 'rgba(255, 255, 255, 0.18)',
      '--glass-border-strong': 'rgba(255, 255, 255, 0.28)',
      '--shadow-lg': '0 12px 32px rgba(12, 18, 40, 0.45)',
      '--shadow-glow': '0 0 24px rgba(124, 157, 255, 0.4)',
      '--radius-lg': '0.875rem',
      '--radius-xl': '1.125rem'
    }
  },
  {
    id: 'neon-arcade',
    name: 'Neon Arcade',
    blurb: 'Cyberpunk cyan & magenta glow on near-black.',
    swatch: 'linear-gradient(135deg, #00e5ff 0%, #7b2ff7 55%, #ff2bd6 100%)',
    fontUrl: FONTS.orbitron,
    vars: {
      '--color-primary': '#00e5ff',
      '--color-primary-dark': '#00b8cc',
      '--color-primary-light': '#5ff0ff',
      '--color-secondary': '#7b2ff7',
      '--color-accent': '#ff2bd6',
      '--color-accent-light': '#ff6be3',
      '--color-dark': '#0a0a14',
      '--color-dark-lighter': '#131326',
      '--color-dark-light': '#1e2446',
      '--color-gray': '#2a3358',
      '--color-gray-light': '#4a5588',
      '--color-light': '#eafcff',
      '--gradient-bg': 'radial-gradient(110% 110% at 50% 0%, #131326 0%, #0a0a14 60%, #050509 100%)',
      '--gradient-dark': 'linear-gradient(135deg, #0a0a14 0%, #131326 100%)',
      '--glass-bg': 'rgba(20, 24, 48, 0.7)',
      '--glass-bg-strong': 'rgba(30, 36, 70, 0.8)',
      '--glass-border': 'rgba(0, 229, 255, 0.4)',
      '--glass-border-strong': 'rgba(0, 229, 255, 0.6)',
      '--shadow-lg': '0 0 24px rgba(0, 229, 255, 0.35)',
      '--shadow-glow': '0 0 24px rgba(0, 229, 255, 0.55)',
      '--radius-lg': '6px',
      '--radius-xl': '10px',
      '--font-display': "'Orbitron', 'Segoe UI', sans-serif"
    }
  },
  {
    id: 'luxe-noir',
    name: 'Luxe Noir',
    blurb: 'Editorial black & gold, collector-grade.',
    swatch: 'linear-gradient(135deg, #c7a566 0%, #9c7c3f 100%)',
    fontUrl: FONTS.playfair,
    vars: {
      '--color-primary': '#c7a566',
      '--color-primary-dark': '#9c7c3f',
      '--color-primary-light': '#d8bd86',
      '--color-secondary': '#9c7c3f',
      '--color-accent': '#e8d7a8',
      '--color-accent-light': '#f1e6c8',
      '--color-dark': '#0a0a0a',
      '--color-dark-lighter': '#141414',
      '--color-dark-light': '#1f1f1f',
      '--color-gray': '#3a3a3a',
      '--color-gray-light': '#5a5a5a',
      '--color-light': '#f5f0e6',
      '--gradient-bg': 'linear-gradient(180deg, #141414 0%, #0a0a0a 100%)',
      '--gradient-dark': 'linear-gradient(135deg, #141414 0%, #1f1f1f 100%)',
      '--glass-bg': 'rgba(255, 255, 255, 0.04)',
      '--glass-bg-strong': 'rgba(255, 255, 255, 0.07)',
      '--glass-border': 'rgba(199, 165, 102, 0.4)',
      '--glass-border-strong': 'rgba(199, 165, 102, 0.6)',
      '--shadow-lg': '0 18px 40px rgba(0, 0, 0, 0.7)',
      '--shadow-glow': '0 0 24px rgba(199, 165, 102, 0.4)',
      '--radius-lg': '4px',
      '--radius-xl': '8px',
      '--font-display': "'Playfair Display', Georgia, serif"
    }
  },
  {
    id: 'sakura-dawn',
    name: 'Sakura Dawn',
    blurb: 'Warm rose & peach light mode. (Beta — light surfaces.)',
    swatch: 'linear-gradient(135deg, #ff7a99 0%, #ffa46b 60%, #7bc4b0 120%)',
    vars: {
      '--color-primary': '#ff7a99',
      '--color-primary-dark': '#e85d7e',
      '--color-primary-light': '#ff9db5',
      '--color-secondary': '#ffa46b',
      '--color-accent': '#7bc4b0',
      '--color-accent-light': '#a0d9c9',
      '--color-dark': '#fdf6ec',
      '--color-dark-lighter': '#fff5f0',
      '--color-dark-light': '#ffece2',
      '--color-gray': '#d8c4c0',
      '--color-gray-light': '#b0a0a0',
      '--color-light': '#4a2c33',
      // Sakura Dawn is light-by-design, so these warm-dark text tokens apply in
      // dark mode. In light mode LIGHT_MODE_VARS is layered on top and overrides
      // --color-light / --color-white with the shared neutral (#241a2e). That's
      // intentional — the neutral flip keeps text legible across every theme.
      '--color-white': '#4a2c33',
      // Surfaces are light in this theme, so the semantic text tokens must flip
      // to warm-dark ink too — otherwise they keep the :root dark-mode whites
      // (variables.css) and render invisible on cream backgrounds.
      '--text-primary': 'rgba(74, 44, 51, 0.95)',
      '--text-muted': 'rgba(74, 44, 51, 0.66)',
      '--text-faint': 'rgba(74, 44, 51, 0.5)',
      '--gradient-bg': 'linear-gradient(160deg, #fff1f3 0%, #ffe9de 50%, #fdf6ec 100%)',
      '--gradient-dark': 'linear-gradient(135deg, #fff5f0 0%, #ffece2 100%)',
      '--glass-bg': 'rgba(255, 255, 255, 0.6)',
      '--glass-bg-strong': 'rgba(255, 255, 255, 0.85)',
      '--glass-border': 'rgba(214, 122, 140, 0.28)',
      '--glass-border-strong': 'rgba(214, 122, 140, 0.45)',
      '--shadow-lg': '0 10px 28px rgba(214, 122, 140, 0.22)',
      '--shadow-glow': '0 0 20px rgba(255, 122, 153, 0.35)',
      '--radius-lg': '1rem',
      '--radius-xl': '1.25rem'
    }
  }
];

export const DEFAULT_THEME_ID = 'classic';

/**
 * Light-mode neutral overrides (accent-agnostic).
 *
 * Modes form a light/dark axis ON TOP of the colour theme: the theme supplies
 * accent/brand tokens (and its dark-mode background), while the mode flips the
 * neutral surface/text/background tokens. Dark mode = the theme's own values
 * (no overrides). Light mode = these. Applied AFTER the theme so they win.
 *
 * NOTE: light mode is best-effort. Components built on semantic tokens (the new
 * shell) flip correctly; older pages that hardcode white text/dark surfaces are
 * being migrated — see docs/frontend/DESIGN_REDESIGN.md.
 */
export const LIGHT_MODE_VARS = {
  '--color-white': '#241a2e',
  '--color-light': '#2a2230',
  '--text-primary': 'rgba(36, 26, 46, 0.95)',
  '--text-muted': 'rgba(36, 26, 46, 0.66)',
  '--text-faint': 'rgba(36, 26, 46, 0.5)',
  '--color-dark': '#f4f1fa',
  '--color-dark-lighter': '#ffffff',
  '--color-dark-light': '#efeaf6',
  '--color-gray': '#cdc6da',
  '--color-gray-light': '#9b93ad',
  '--gradient-bg': 'linear-gradient(160deg, #f5f2fc 0%, #eef1f8 60%, #f7f3fb 100%)',
  '--gradient-dark': 'linear-gradient(135deg, #ffffff 0%, #efeaf6 100%)',
  '--glass-bg': 'rgba(255, 255, 255, 0.72)',
  '--glass-bg-strong': 'rgba(255, 255, 255, 0.92)',
  '--glass-border': 'rgba(30, 24, 50, 0.12)',
  '--glass-border-strong': 'rgba(30, 24, 50, 0.22)',
  '--shadow-lg': '0 12px 32px rgba(60, 50, 90, 0.16)',
  '--shadow-glow': '0 0 20px rgba(60, 50, 90, 0.12)'
};

export const MODES = [
  { id: 'dark', name: 'Dark', icon: '🌙' },
  { id: 'light', name: 'Light', icon: '☀️' }
];

export const DEFAULT_MODE = 'dark';

/** Union of every CSS var any theme/mode manages — used to fully reset between selections. */
export const MANAGED_VARS = Array.from(
  THEMES.reduce((set, theme) => {
    Object.keys(theme.vars).forEach((k) => set.add(k));
    return set;
  }, new Set(Object.keys(LIGHT_MODE_VARS)))
);

export const DENSITIES = [
  { id: 'comfortable', name: 'Comfortable', blurb: 'Spacious — the original layout.' },
  { id: 'compact', name: 'Compact', blurb: 'Tighter chrome, more content per screen.' }
];

export const DEFAULT_DENSITY_ID = 'comfortable';
